import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Popconfirm, message, Typography, Descriptions, Space, Input, Modal } from 'antd';
import { DeleteOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useGetInquiriesMutation, useDeleteInquiryMutation } from '../redux/services/apiSlice';

const { Title } = Typography;

const Inquiries = () => {
    const [getInquiries, { data, isLoading }] = useGetInquiriesMutation();
    const [deleteInquiry] = useDeleteInquiryMutation();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [searchText, setSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState('-createdAt');

    const fetchInquiries = useCallback((page = 1, limit = 10, search = '', sort = '-createdAt') => {
        getInquiries({ page, limit, search, sort });
    }, [getInquiries]);

    useEffect(() => {
        fetchInquiries(pagination.current, pagination.pageSize, searchText, sortOrder);
    }, [fetchInquiries, pagination.current, pagination.pageSize, searchText, sortOrder]);

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
        if (sorter.field) {
            const order = sorter.order === 'ascend' ? sorter.field : `-${sorter.field}`;
            setSortOrder(order);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination({ ...pagination, current: 1 });
    };

    const handleDelete = async (id) => {
        try {
            await deleteInquiry(id).unwrap();
            message.success('Inquiry deleted');
            fetchInquiries(pagination.current, pagination.pageSize, searchText, sortOrder);
        } catch (error) {
            message.error('Error deleting');
        }
    };

    const showDetails = (record) => {
        Modal.info({
            title: 'Inquiry Details',
            width: 600,
            content: (
                <Descriptions bordered column={1}>
                    <Descriptions.Item label="Name">{record.name}</Descriptions.Item>
                    <Descriptions.Item label="Email">{record.email}</Descriptions.Item>
                    <Descriptions.Item label="Mobile">{record.mobileNo}</Descriptions.Item>
                    <Descriptions.Item label="Country">{record.country}</Descriptions.Item>
                    <Descriptions.Item label="Adults">{record.adults}</Descriptions.Item>
                    <Descriptions.Item label="Children">{record.children}</Descriptions.Item>
                    <Descriptions.Item label="Origin">{record.origin}</Descriptions.Item>
                    <Descriptions.Item label="Destination">{record.destination}</Descriptions.Item>
                    <Descriptions.Item label="Message">{record.message}</Descriptions.Item>
                </Descriptions>
            ),
        });
    };

    const columns = [
        { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: (date) => new Date(date).toLocaleDateString(), sorter: true },
        { title: 'Name', dataIndex: 'name', key: 'name', sorter: true },
        { title: 'Destination', dataIndex: 'destination', key: 'destination', sorter: true },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => showDetails(record)} size="small" />
                    <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record._id)}>
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Booking Inquiries</Title>
                <Input.Search
                    placeholder="Search inquiries..."
                    onSearch={handleSearch}
                    style={{ width: 300 }}
                    enterButton={<SearchOutlined />}
                />
            </div>
            <Table 
                dataSource={data?.inquiries || []} 
                columns={columns} 
                rowKey="_id" 
                loading={isLoading}
                pagination={{
                    current: data?.currentPage || 1,
                    pageSize: pagination.pageSize,
                    total: data?.totalItems || 0,
                    showSizeChanger: true
                }}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default Inquiries;
